// src/services/eligibilityChecklist.js
//
// SourceX — the study's INCLUSION/EXCLUSION CRITERIA checklist.
//
// A third kind of standing page, beside the visits and the logs. It is the
// numbered inclusion and exclusion criteria copied VERBATIM out of the protocol,
// with a Yes / No / N/A box against each one, and a fixed screening + final
// eligibility sign-off block at the foot. A coordinator prints it once per
// patient, works down it on the day of screening, and files it.
//
// This file is DATA + ONE BUILDER, exactly like sourceLogTemplates.js — read
// that file's header first, it sets the house style for this kind of module.
// Nothing here is Firebase- or React-aware, so it is unit-testable on its own
// and the shape a checklist takes is stated in ONE place rather than being
// assembled inline by whichever page happens to add one.
//
// TRANSCRIBED FROM THE SOURCE FORM
//
// Every string below is lifted from `Eligibility Checklist V1.1.docx.md` in the
// repo root — the form this replaces. The wording of the two attestations in
// particular is what an investigator is signing, so it is quoted, never
// paraphrased. `the closing block matches the source form` in the test file
// checks each one back against that document.
//
// WHY THE CRITERIA ARE A TABLE, AND WHY IT IS FIXED-ROW
//
// The checklist is an ordinary `Table` question in an ordinary event, which is
// what buys it approval, versioning, the editor's grid, the patient's fill-in,
// both exporters and stripAnswers() for free — the same trade sourceLogTemplates
// makes.
//
// It uses `rowLabels` (the criterion NUMBERS) rather than a plain repeatable
// table because the rows are part of the FORM: criterion 14 is criterion 14
// whether or not anybody has ticked a box on it. Only a fixed-row table prints
// its `prefillRows` on the patient's copy as well as the blank one — a plain
// table drops them the moment real answers exist, which would have printed a
// page of empty boxes with no criteria beside them.
//
// LAYOUT — five columns, as the paper form has:
//
//   #          the criterion number      rowLabels, ROW_LABEL_WIDTH_PCT wide
//   Criteria   the verbatim text         `structural`, so it prints as form
//                                        text and not as a bold pre-filled
//                                        answer (see makeColumn)
//   Yes/No/N/A one tick box each         Checkbox columns, centred in the cell
//
// The Criteria column's HEADER carries the table's own title ("Inclusion
// Criteria (must be yes to qualify)"), because a table header repeats at the top
// of every page it runs onto in both exporters — so every page says what it is,
// which is requirement 4 of the brief, at no cost.
//
// Sub-headings ("Medical Conditions:") are `groupRows`, not rows: they take no
// number and must never be mistakable for a criterion. See makeQuestion.

import { normFieldType } from "./templateSchema";

/** The section AND event name. One per template — see SectionEditor.addEligibility. */
export const ELIGIBILITY_SECTION_NAME = "Inclusion/Exclusion Criteria";

/** The grey band at the top of the page. */
export const ELIGIBILITY_HEADING = "Inclusion/Exclusion Criteria";

/**
 * What the REPEATING page header calls this document, on every page.
 *
 * A visit's header names its visit; this names itself the same way. The centred
 * title only prints on page one, so without this a filed page two carried a
 * grid of tick boxes with nothing on it saying what they were for.
 */
export const CRITERIA_PAGE_TITLE = "Inclusion/Exclusion Checklist";

/** The single question above the two grids. */
export const REVIEWED_QUESTION =
  "Inclusion/ Exclusion criteria for Screening visit reviewed with the patient?";

/** The two grids' titles, which are also their Criteria column's header. */
export const INCLUSION_TABLE_HEADER = "Inclusion Criteria (must be yes to qualify)";
export const EXCLUSION_TABLE_HEADER = "Exclusion Criteria (must be no to qualify)";

/** The sub-headings the protocol's exclusion list is usually grouped under. */
export const CRITERIA_GROUPS = Object.freeze([
  "Medical Conditions:",
  "Medications/Treatments:",
  "Other:",
]);

// Column geometry. The three tick-box columns are as narrow as a box plus a
// little air — the brief's "only enough space a box needs with a little extra
// room" — and the criteria column takes everything left over. Percentages of the
// table's DATA area; the "#" column is separate (see ROW_LABEL_WIDTH_PCT) and is
// reserved off the top before these divide what remains, exactly as a log's
// "No." column is.
const CRITERIA_COL_WIDTH = 76;
const BOX_COL_WIDTH = 8;

/** Share of the page the "#" column takes. Wide enough for two digits. */
export const ROW_LABEL_WIDTH_PCT = 5;

/** Writing space per row, in points. A criterion wraps to two or three lines. */
const CRITERIA_ROW_HEIGHT_PT = 24;

/** The criteria grids' headers ARE the form, so they print at 10pt, not 9. */
const CRITERIA_HEADER_FONT_PT = 10;

// The closing block, verbatim from the source form.
//
// Structured rather than pre-rendered so BOTH exporters draw it from the same
// data and cannot drift — the same reason LOG_CLOSING_LABEL lives with the logs.
// Each block is one of:
//   heading      bold, its own line
//   attestation  italic, smaller — what the signature below it means
//   question     bold, followed by its tick boxes
//   signature    "<label> ____  Date: ____"
export const ELIGIBILITY_CLOSING = Object.freeze([
  { kind: "heading", text: "Initial Screening Review:" },
  {
    kind: "attestation",
    text:
      "Signature confirms that the inclusion and exclusion criteria were reviewed the day of screening based on the information available at that time.",
  },
  {
    kind: "question",
    text: "Was Screening criteria met?",
    options: ["Yes", "No"],
  },
  { kind: "signature", label: "Staff Signature:" },
  { kind: "signature", label: "Investigator/Sub-Investigator Signature:" },
  { kind: "heading", text: "Final Eligibility Review:" },
  {
    kind: "attestation",
    text:
      "Signature confirms that all required eligibility information has been reviewed and the subject has either been determined to be a screen failure or confirmed to meet all eligibility criteria prior to randomization.",
  },
  {
    kind: "question",
    text: "Were final eligibility criteria met prior to Randomization?",
    // The third option carries its own parenthetical on the paper form. Kept as
    // one option string rather than an option plus a note, because that is one
    // box with one meaning: the patient screen-failed and nobody signs.
    options: [
      "Yes",
      "No",
      "N/A (Screen failed day of screening *signature not required)",
    ],
  },
  { kind: "signature", label: "Staff Signature:" },
  { kind: "signature", label: "Investigator/Sub-Investigator Signature:" },
]);

/**
 * @typedef {{kind: "group", label: string} | {kind: "criterion", text: string}} CriteriaItem
 *
 * The unit the extraction returns and this file consumes. A `group` is a
 * sub-heading in the protocol's own list; a `criterion` is one numbered rule.
 * Keeping them in ONE ordered list, rather than criteria plus a separate map of
 * headings, is what makes "the heading goes where the protocol put it" the only
 * possible outcome.
 */

/** Is this a heading rather than a criterion? Tolerant of a missing `kind`. */
const isGroup = (item) =>
  !!item && (item.kind === "group" || (!item.text && !!item.label));

const itemText = (item) => String((item && (item.text ?? item.label)) || "").trim();

/**
 * One criteria grid — inclusion or exclusion.
 *
 * @param {object} args
 * @param {string} args.header  the grid's title, which is also its Criteria
 *   column header, so it repeats on every page the grid runs onto
 * @param {CriteriaItem[]} args.items  criteria and sub-headings, in the
 *   protocol's own order
 * @param {string} args.idPrefix  column ids are keys into `prefillRows` and into
 *   a patient's recorded answers, so the two grids must not share them
 * @returns {object} a makeQuestion-shaped seed (no ids assigned yet)
 */
export const buildCriteriaTable = ({ header, items = [], idPrefix, emphasize }) => {
  const rowLabels = [];
  const prefillRows = [];
  const groupRows = {};

  (Array.isArray(items) ? items : []).forEach((item) => {
    if (!item) return;
    const text = itemText(item);
    if (isGroup(item)) {
      // A heading with no words is nothing at all; a CRITERION with no words is
      // a blank row to write in, which is the whole of the empty scaffold.
      if (!text) return;
      // Attached to the row it PRECEDES, which is the next criterion to be
      // pushed. A heading with nothing after it is dropped rather than left
      // pointing past the end of the table, where neither exporter would draw
      // it. Two headings in a row collapse to the later one, for the same
      // reason: only one can precede a given row.
      groupRows[rowLabels.length] = text;
      return;
    }
    prefillRows.push(text ? { [`${idPrefix}_criteria`]: text } : {});
    rowLabels.push(String(rowLabels.length + 1));
  });

  const orphaned = Object.keys(groupRows).filter(
    (k) => Number(k) >= rowLabels.length
  );
  orphaned.forEach((k) => delete groupRows[k]);

  return {
    type: "Table",
    question: header,
    rowLabels,
    rowLabelHeader: "#",
    rowLabelWidthPct: ROW_LABEL_WIDTH_PCT,
    ...(Object.keys(groupRows).length ? { groupRows } : {}),
    prefillRows,
    minRows: rowLabels.length,
    maxRows: rowLabels.length,
    rowHeightPt: CRITERIA_ROW_HEIGHT_PT,
    headerFontPt: CRITERIA_HEADER_FONT_PT,
    columns: [
      {
        id: `${idPrefix}_criteria`,
        label: header,
        type: "Text",
        width: CRITERIA_COL_WIDTH,
        // The criterion text is the FORM, not an answer somebody pre-entered.
        // Without this the whole page prints bold. See makeColumn.
        structural: true,
        // "must be YES to qualify" / "must be NO to qualify" — the one word
        // that tells the two grids apart, printed bold and underlined. See
        // emphasisParts.
        ...(emphasize ? { emphasize } : {}),
      },
      { id: `${idPrefix}_yes`, label: "Yes", type: "Checkbox", width: BOX_COL_WIDTH },
      { id: `${idPrefix}_no`, label: "No", type: "Checkbox", width: BOX_COL_WIDTH },
      { id: `${idPrefix}_na`, label: "N/A", type: "Checkbox", width: BOX_COL_WIDTH },
    ],
  };
};

/**
 * How many blank rows the scaffold gets when there is no protocol to read from.
 * The counts on the paper form — enough that a coordinator typing the criteria
 * in by hand rarely has to add rows, and few enough that they aren't deleting a
 * page of empties.
 */
const BLANK_INCLUSION_ROWS = 12;
const BLANK_EXCLUSION_ROWS = 34;

const blankRows = (n) =>
  Array.from({ length: n }, () => ({ kind: "criterion", text: "" }));

/**
 * The empty checklist — the structure with no criteria in it.
 *
 * Used when the study has no protocol on file, or when the extraction fails.
 * A coordinator gets the finished form and types the criteria into the grid,
 * which is exactly what they do today; what they don't get is a dead end.
 *
 * The exclusion list carries the paper form's three sub-headings at the paper
 * form's own row numbers, so the shape is recognisable before anything is typed.
 */
export const EMPTY_ELIGIBILITY = Object.freeze({
  inclusion: blankRows(BLANK_INCLUSION_ROWS),
  exclusion: [
    { kind: "group", label: CRITERIA_GROUPS[0] },
    ...blankRows(13),
    { kind: "group", label: CRITERIA_GROUPS[1] },
    ...blankRows(14),
    { kind: "group", label: CRITERIA_GROUPS[2] },
    ...blankRows(BLANK_EXCLUSION_ROWS - 27),
  ],
});

/**
 * The section + event the checklist becomes when it is added to a template.
 *
 * Mirrors buildLogSection: framework- and Firebase-free, factories passed in
 * rather than imported, so this module stays a leaf of the schema and the shape
 * lives in one place.
 *
 * @param {{inclusion: CriteriaItem[], exclusion: CriteriaItem[]}} criteria
 * @param {{makeSection: Function}} factories
 * @returns {object} a makeSection-shaped section
 */
export const buildEligibilitySection = (criteria, { makeSection }) => {
  const inclusion = criteria?.inclusion?.length
    ? criteria.inclusion
    : EMPTY_ELIGIBILITY.inclusion;
  const exclusion = criteria?.exclusion?.length
    ? criteria.exclusion
    : EMPTY_ELIGIBILITY.exclusion;

  const questions = [
    // The grey band. `Header` is already how both exporters draw one.
    { type: "Header", question: ELIGIBILITY_HEADING },
    {
      // A Select (pick one), with SQUARE marks because that is what the paper
      // form draws — the documented opt-out in markStyleFor, same as the
      // Medical/Surgical History log's opening question.
      type: "Select",
      markStyle: "box",
      question: REVIEWED_QUESTION,
      options: ["Yes", "No"],
    },
    buildCriteriaTable({
      header: INCLUSION_TABLE_HEADER,
      items: inclusion,
      idPrefix: "inc",
      emphasize: "yes",
    }),
    buildCriteriaTable({
      header: EXCLUSION_TABLE_HEADER,
      items: exclusion,
      idPrefix: "exc",
      emphasize: "no",
    }),
  ];

  return makeSection({
    name: ELIGIBILITY_SECTION_NAME,
    kind: "criteria",
    events: [
      {
        eventName: ELIGIBILITY_SECTION_NAME,
        kind: "criteria",
        // No `orientation`: five columns fit portrait, and the paper form is
        // portrait. A log is landscape because a log is 6-10 columns wide.
        questions: questions.map((q) => ({ ...q, type: normFieldType(q.type) })),
      },
    ],
  });
};
